import type {AppRole} from "./roles";

export type AuthScope = "staff" | "merchant";

interface AuthStorageKeys {
  accessToken: string;
  refreshToken: string;
}

export interface StoredAuthTokens {
  accessToken: string | null;
  refreshToken: string | null;
}

export const STAFF_AUTH_STORAGE_KEYS: Readonly<AuthStorageKeys> = {
  accessToken: "token",
  refreshToken: "refreshToken",
};

export const MERCHANT_AUTH_STORAGE_KEYS: Readonly<AuthStorageKeys> = {
  accessToken: "merchantToken",
  refreshToken: "merchantRefreshToken",
};

const STORAGE_KEYS: Readonly<Record<AuthScope, Readonly<AuthStorageKeys>>> = {
  staff: STAFF_AUTH_STORAGE_KEYS,
  merchant: MERCHANT_AUTH_STORAGE_KEYS,
};

export function authScopeForRoute(route: string): AuthScope {
  const queryStart = route.indexOf("?");
  const path = queryStart === -1 ? route : route.slice(0, queryStart);
  return path === "#/merchant" || path.startsWith("#/merchant/")
    ? "merchant"
    : "staff";
}

export function isRoleAllowedForAuthScope(
  scope: AuthScope,
  role: AppRole,
): boolean {
  return scope === "merchant"
    ? role === "merchant"
    : role === "admin" || role === "trader";
}

export function readStoredAuthTokens(scope: AuthScope): StoredAuthTokens {
  const keys = STORAGE_KEYS[scope];
  try {
    return {
      accessToken: sessionStorage.getItem(keys.accessToken),
      refreshToken: sessionStorage.getItem(keys.refreshToken),
    };
  } catch {
    return {accessToken: null, refreshToken: null};
  }
}

export function writeStoredAuthTokens(
  scope: AuthScope,
  accessToken: string,
  refreshToken: string | null,
): void {
  const keys = STORAGE_KEYS[scope];
  try {
    sessionStorage.setItem(keys.accessToken, accessToken);
    if (refreshToken) {
      sessionStorage.setItem(keys.refreshToken, refreshToken);
    } else {
      sessionStorage.removeItem(keys.refreshToken);
    }
  } catch {
    /* no-op */
  }
}

export function clearStoredAuthTokens(scope: AuthScope): void {
  const keys = STORAGE_KEYS[scope];
  try {
    sessionStorage.removeItem(keys.accessToken);
    sessionStorage.removeItem(keys.refreshToken);
  } catch {
    /* no-op */
  }
}
